import styles from "./AssistantCreate.module.css";
import Teams from "../components/Teams";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectTeams } from "../redux/slices/teamsSlice";
import { selectUser } from "../redux/slices/userSlice";
import {
  selectAssistants,
  fetchAssistantDefault,
  clearAssistantsError,
  setCreateForm,
  setError,
  fetchFileOptions,
  createAssistant,
  clearCreateResult,
} from "../redux/slices/assistantsSlice.js";
import { useToaster } from "../components/Toaster";
import Select from "react-select";
import FileModal from "../components/FileModal";
import { useNavigate } from "react-router-dom";

export default function AssistantCreate() {
  const teams = useSelector(selectTeams);
  const user = useSelector(selectUser);
  const assistants = useSelector(selectAssistants);
  const dispatch = useDispatch();
  const { push } = useToaster();
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const hasMounted = useRef(false);
  const navigate = useNavigate();

  const assistantDefaultOption = (assistants?.assistant_default ?? []).map((item) => ({
    value: item.id,
    label: item.option_name,
  }));

  const isDisabled = useMemo(() => {
    const selected = teams.teams?.find((item) => item.token === teams.team_selected);
    const role = selected?.account_role;
    return Boolean((teams.teams?.length ?? 0) > 0 && ![1, 2].includes(role));
  }, [teams.teams, teams.team_selected]);

  useEffect(() => {
    dispatch(fetchAssistantDefault());
  }, [dispatch]);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    if (teams?.team_selected != assistants?.createForm?.team_token) {
      dispatch(
        setCreateForm({
          team_token: teams?.team_selected,
          name: "",
          description: "",
          instructions: "",
          assistant_default: null,
          files: [],
        })
      );
    }

    if (teams?.no_teams == false && (teams?.team_selected ?? "") !== "" && (user?.data?.token ?? "") !== "") {
      dispatch(
        fetchFileOptions({
          team_token: teams.team_selected,
          account_token: user.data.token,
        })
      );
    }
  }, [teams?.no_teams, teams?.team_selected, user?.data?.token, dispatch]);

  useEffect(() => {
    const allowed = new Set((assistants?.fileOptions ?? []).map((x) => x.file_token));
    const current = assistants?.createForm?.files ?? [];
    const filtered = current.filter((t) => allowed.has(t));
    const same =
      current.length === filtered.length &&
      current.every((v, i) => v === filtered[i]);

    if (same) return;
    if (!assistants?.createForm) return;

    dispatch(
      setCreateForm({
        ...assistants.createForm,
        files: filtered,
      })
    );
  }, [assistants?.fileOptions, dispatch]);

  useEffect(() => {
    const e = assistants.error;
    const msg = (e && (e.msg || e.message)) || "";
    if (!msg) return;

    push({ title: "發生錯誤", message: msg, variant: "danger" });
    dispatch(clearAssistantsError());
  }, [assistants.error, push, dispatch]);

  useEffect(() => {
    const r = assistants.createResult;
    const ok = Boolean(r && Object.prototype.hasOwnProperty.call(r, "token"));
    if (!ok) return;

    push({ title: "建立完成", message: "已成功建立代理人", variant: "success" });

    dispatch(clearCreateResult());

    dispatch(
      setCreateForm({
        team_token: teams?.team_selected,
        name: "",
        description: "",
        instructions: "",
        assistant_default: null,
        files: [],
      })
    );

    navigate("/", { replace: true });
  }, [assistants.createResult, push, dispatch, teams?.team_selected, navigate]);

  const handleInputChange = (e) => {
    dispatch(
      setCreateForm({
        ...assistants.createForm,
        [e.target.name]: e.target.value,
      })
    );
  };

  const handleSubmit = () => {
    if (!assistants.createForm?.name) {
      dispatch(setError({ msg: "請輸入代理人名稱！" }));
      return;
    }

    if (!assistants.createForm?.assistant_default) {
      dispatch(setError({ msg: "請選擇代理人類型！" }));
      return;
    }

    if (!assistants.createForm?.instructions) {
      dispatch(setError({ msg: "請輸入系統指令（Prompt）！" }));
      return;
    }

    dispatch(
      createAssistant({
        ...assistants.createForm,
        account_token: user.data.token,
      })
    );
  };

  const onAssistantDefaultChange = (opt) => {
    dispatch(
      setCreateForm({
        ...assistants.createForm,
        assistant_default: opt?.value || "",
      })
    );
  };

  return (
    <>
      <section className={styles.teamsWrap}>
        <div className={`${styles.panel} mt-2`}>
          <div className={`px-2 px-md-5 py-2 py-md-5 ${styles.content} row g-4`}>
            <div className="col-12">
              <div className={`p-md-4 ${styles.block}`}>
                <h4 className={styles.sectionTitle}>
                  <i className="bi bi-diagram-3 me-2" />
                  團隊選擇
                </h4>
                <div className="mt-2">
                  <Teams />
                </div>
              </div>
            </div>

            <div className="col-12 mt-2 mt-md-4">
              <div className={`p-md-4 ${styles.block}`}>
                <h4 className={styles.sectionTitle}>
                  <i className="bi-magic me-2" />
                  建立代理人
                </h4>

                <form className={styles.form}>
                  <div className="mb-3">
                    <label htmlFor="assistant-create_name" className={`${styles.fieldLabel} form-label`}>
                      <span className={styles.labelDot} aria-hidden="true" />
                      代理人名稱 <span className="text-danger">*</span>
                    </label>

                    <input
                      type="text"
                      className={`form-control ${styles.input}`}
                      id="assistant-create_name"
                      placeholder="輸入代理人名稱"
                      name="name"
                      value={assistants?.createForm?.name ?? ""}
                      onChange={handleInputChange}
                      maxLength={20}
                      disabled={isDisabled}
                    />

                    <div className={styles.helpRow}>
                      <small className={styles.helpText}>最多 20 字。</small>
                      <small className={styles.charCount}>{(assistants?.createForm?.name ?? "").length}/20</small>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="assistant-create_assistant_default" className={`${styles.fieldLabel} form-label`}>
                      <span className={styles.labelDot} aria-hidden="true" />
                      代理人類型 <span className="text-danger">*</span>
                    </label>

                    <Select
                      inputId="assistant-create_assistant_default"
                      instanceId="assistant-create_assistant_default"
                      className={styles.select}
                      options={assistantDefaultOption}
                      value={assistantDefaultOption.find((o) => o.value === assistants.createForm.assistant_default) || null}
                      onChange={onAssistantDefaultChange}
                      placeholder="選擇代理人類型"
                      isSearchable
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      isDisabled={isDisabled}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 2200 }),
                        control: (base, state) => ({
                          ...base,
                          backgroundColor: state.isDisabled ? "#eef0f5" : "#ffffff",
                          borderColor: state.isFocused ? "#d84040" : "#d0d3d8",
                          boxShadow: state.isFocused
                            ? "inset 0 1px 2px rgba(0,0,0,.10), 0 0 0 2px #fff, 0 0 0 4px rgba(255,64,64,.5)"
                            : "inset 0 1px 2px rgba(0,0,0,.08)",
                          borderRadius: 8,
                          minHeight: 40,
                          outline: "none",
                          transition: "box-shadow .2s ease, border-color .2s ease",
                          cursor: state.isDisabled ? "not-allowed" : "default",
                          opacity: state.isDisabled ? 0.85 : 1,
                          ":hover": {
                            borderColor: state.isFocused ? "#d84040" : "rgba(0,0,0,.2)",
                          },
                        }),
                        singleValue: (base, state) => ({
                          ...base,
                          color: state.isDisabled ? "rgba(16,16,20,.55)" : "#101014",
                        }),
                        placeholder: (base) => ({ ...base, color: "#868e96" }),
                        dropdownIndicator: (base, state) => ({
                          ...base,
                          color: state.isFocused ? "#d84040" : "#495057",
                          ":hover": { color: "#d84040" },
                        }),
                        indicatorSeparator: (base) => ({ ...base, backgroundColor: "rgba(0,0,0,.12)" }),
                        menu: (base) => ({
                          ...base,
                          overflow: "hidden",
                          marginTop: 6,
                          borderRadius: 8,
                          backgroundColor: "#ffffff",
                          border: "1px solid #d84040",
                          boxShadow: "0 18px 36px rgba(0,0,0,.35), 0 0 0 1px rgba(0,0,0,.1)",
                          zIndex: 2200,
                        }),
                        menuList: (base) => ({
                          ...base,
                          padding: 4,
                          backgroundColor: "#ffffff",
                        }),
                        option: (base, state) => ({
                          ...base,
                          padding: "10px 14px",
                          borderRadius: 6,
                          transition: "background .15s ease, color .15s ease, box-shadow .15s ease",
                          fontWeight: state.isSelected ? 700 : 500,
                          color: state.isDisabled ? "#ccc" : "#101014",
                          backgroundColor: state.isSelected || state.isFocused ? "rgba(216,64,64,.1)" : "transparent",
                          boxShadow: state.isSelected || state.isFocused ? "inset 4px 0 0 0 #d84040" : "none",
                          cursor: state.isDisabled ? "not-allowed" : "default",
                          ":active": {
                            backgroundColor: state.isDisabled ? "transparent" : "rgba(216,64,64,.14)",
                          },
                        }),
                      }}
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="assistant-create_description" className={`${styles.fieldLabel} form-label`}>
                      <span className={styles.labelDot} aria-hidden="true" />
                      代理人說明
                    </label>

                    <input
                      type="text"
                      className={`form-control ${styles.input}`}
                      id="assistant-create_description"
                      placeholder="輸入代理人說明"
                      name="description"
                      value={assistants?.createForm?.description ?? ""}
                      onChange={handleInputChange}
                      maxLength={50}
                      disabled={isDisabled}
                    />

                    <div className={styles.helpRow}>
                      <small className={styles.helpText}>最多 50 字。例如：用於回答客戶在官網上遇到的問題</small>
                      <small className={styles.charCount}>{(assistants?.createForm?.description ?? "").length}/50</small>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="assistant-create_instructions" className={`${styles.fieldLabel} form-label`}>
                      <span className={styles.labelDot} aria-hidden="true" />
                      系統指令（Prompt） <span className="text-danger">*</span>
                    </label>

                    <textarea
                      className={`form-control ${styles.input}`}
                      id="assistant-create_instructions"
                      name="instructions"
                      placeholder="請輸入 AI 的系統指令，例如：你是一位擅長客服支援的專業助理，請用親切口吻回答使用者問題..."
                      rows={6}
                      maxLength={1000}
                      value={assistants?.createForm?.instructions ?? ""}
                      onChange={handleInputChange}
                      disabled={isDisabled}
                    />

                    <div className={styles.helpRow}>
                      <small className={styles.helpText}>最多 1000 字。建議清楚描述 AI 的角色、風格與限制。</small>
                      <small className={styles.charCount}>{(assistants?.createForm?.instructions ?? "").length}/1000</small>
                    </div>
                  </div>

                  <div className="mb-0">
                    <label htmlFor="assistant-create_files" className={`${styles.fieldLabel} form-label`}>
                      <span className={styles.labelDot} aria-hidden="true" />
                      背景知識
                    </label>
                  </div>

                  <div className="mb-3">
                    <button
                      type="button"
                      id="assistant-create_files"
                      className={styles.btnUpload}
                      onClick={() => setFileModalOpen(true)}
                      disabled={isDisabled}
                    >
                      <i className="bi bi-list-check me-2" />
                      選擇檔案
                    </button>

                    <div className={styles.helpRow}>
                      <small className={styles.helpText}>
                        目前共選擇 <span style={{ color: "red" }}>{(assistants?.createForm?.files ?? []).length}</span> 個檔案。
                      </small>
                    </div>
                  </div>

                  <div className={styles.actions}>
                    <button type="button" className={styles.btnSave} onClick={handleSubmit} disabled={isDisabled}>
                      <i className="bi bi-send-check me-1" />
                      送出
                    </button>
                  </div>
                </form>
              </div>
            </div>

          </div>
        </div>
      </section>

      <FileModal modalOpen={fileModalOpen} onHide={() => setFileModalOpen(false)} />
    </>
  );
}
